<template>
  <div class="input-type-selector">
    <h3 class="input-type-selector__title">
      🗂️ {{ t("pick_a_source_to_get_started") }}
    </h3>
    <div class="input-type-selector__grid">
      <div
        v-for="inputType in inputTypes"
        :key="inputType.value"
        class="input-type-card"
        :class="{
          'input-type-card--selected': selectedType === inputType.value,
        }"
        @click="selectType(inputType.value)"
      >
        <div class="input-type-card__icon" v-html="inputType.icon"></div>
        <span class="input-type-card__label">{{ inputType.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { MiaInputType } from "../../types";
import { useI18n } from "../../i18n";

export default {
  name: "InputTypeSelector",
  props: {
    selectedType: {
      type: String,
      default: null,
    },
  },
  emits: ["select", "image-select", "audio-select"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const inputTypes = computed(() => [
      {
        value: MiaInputType.TEXT,
        label: t("text"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polyline points="4,7 4,4 20,4 20,7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>`,
      },
      {
        value: MiaInputType.IMAGE,
        label: t("image"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21,15 16,10 5,21" />
        </svg>`,
      },
      {
        value: MiaInputType.AUDIO,
        label: t("audio"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
          <path d="M19 10v2a7 7 0 01-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>`,
      },
      {
        value: MiaInputType.FILE,
        label: t("file"),
        icon: `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14,2 14,8 20,8" />
        </svg>`,
      },
    ]);

    const selectType = (type) => {
      emit("select", type);
    };

    return {
      t,
      inputTypes,
      selectType,
    };
  },
};
</script>

<style scoped>
.input-type-selector {
  padding: 0 12px;
}

.input-type-selector__title {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 12px 0;
}

.input-type-selector__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 16px;
}

.input-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  background: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.input-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.input-type-card--selected {
  border-color: var(--mia-primary);
  background: linear-gradient(
    145deg,
    rgba(0, 196, 68, 0.1),
    rgba(0, 196, 68, 0.16)
  );
}

.input-type-card__icon {
  color: var(--mia-primary);
  margin-bottom: 8px;
}

.input-type-card__label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

@media (max-width: 640px) {
  .input-type-selector__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .input-type-selector__title,
[data-theme="dark"] .input-type-selector__title,
.dark .input-type-selector__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .input-type-selector__grid,
[data-theme="dark"] .input-type-selector__grid,
.dark .input-type-selector__grid {
  /* Grid layout is already responsive, just ensure proper spacing in dark mode */
  gap: 12px;
}

@media (max-width: 640px) {
  [data-bs-theme="dark"] .input-type-selector__grid,
  [data-theme="dark"] .input-type-selector__grid,
  .dark .input-type-selector__grid {
    gap: 10px;
  }
}
</style>
